import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../axios/axiosconf';
import Swal from 'sweetalert2';
import ModalConfigurarSispro from './ModalConfigurarSispro';
import { getEtiquetaFactura } from '../../../utils/etiquetaFactura';
import { pacienteTieneDatosCompletosParaFactura } from '../../../utils/busquedaPacienteFacturacion';

function ModalRipsExito({ show, onClose, ripsData, onDescargar, onEnvioExitoso }) {
    const [enviando, setEnviando] = useState(false);
    const [facturaValida, setFacturaValida] = useState(null);
    const [validandoFactura, setValidandoFactura] = useState(false);
    const [estadoEnvio, setEstadoEnvio] = useState(null); // null, 'enviado', 'error'
    const [cuv, setCuv] = useState(null);
    const [fechaRadicacion, setFechaRadicacion] = useState(null);
    const [puedeGestionarSispro, setPuedeGestionarSispro] = useState(false);
    const [showModalErrorSispro, setShowModalErrorSispro] = useState(false);
    const [errorSispro, setErrorSispro] = useState(null);
    const [showModalConfigSispro, setShowModalConfigSispro] = useState(false);
    const [pacienteDatos, setPacienteDatos] = useState(null);
    const [cargandoPaciente, setCargandoPaciente] = useState(false);

    useEffect(() => {
        if (show && ripsData && ripsData.factura_id) {
            validarEstadoFactura();
        } else if (show && ripsData && !ripsData.factura_id) {
            setFacturaValida({ valida: true, motivo: null });
        }
    }, [show, ripsData]);

    useEffect(() => {
        if (!show || !ripsData) {
            setPacienteDatos(null);
            return;
        }

        const pacienteId = ripsData.paciente_id;
        let cancelled = false;

        const aplicarDatos = (datos) => {
            if (!cancelled) setPacienteDatos(datos || null);
        };

        // Si ya vienen datos y están completos, úsalos; si no, recarga por id
        if (ripsData.paciente_datos && pacienteTieneDatosCompletosParaFactura(ripsData.paciente_datos)) {
            aplicarDatos(ripsData.paciente_datos);
            return;
        }

        if (!pacienteId) {
            aplicarDatos(ripsData.paciente_datos || null);
            return;
        }

        setCargandoPaciente(true);
        axios.get('/paciente', { params: { id: pacienteId } })
            .then((response) => {
                if (cancelled) return;
                const datos = Array.isArray(response.data) ? response.data[0] : response.data;
                setPacienteDatos(datos || ripsData.paciente_datos || null);
            })
            .catch(() => {
                if (!cancelled) setPacienteDatos(ripsData.paciente_datos || null);
            })
            .finally(() => {
                if (!cancelled) setCargandoPaciente(false);
            });

        return () => { cancelled = true; };
    }, [show, ripsData]);

    useEffect(() => {
        if (!show || !ripsData) {
            return;
        }

        const yaEnviado = ripsData.sispro_enviado === 1
            || ripsData.sispro_enviado === true
            || ripsData.sispro_enviado === '1';

        if (yaEnviado) {
            setEstadoEnvio('enviado');
            setCuv(ripsData.sispro_cuv || null);
            setFechaRadicacion(ripsData.sispro_fecha_radicacion || null);
        } else {
            setEstadoEnvio(null);
            setCuv(null);
            setFechaRadicacion(null);
        }
    }, [show, ripsData]);

    useEffect(() => {
        if (!show) return;
        let userdata = {};
        try {
            userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
        } catch (e) {
            userdata = {};
        }

        // Permiso para enviar RIPS a SISPRO
        let tienePermiso = false;
        if (userdata.permissions && Array.isArray(userdata.permissions)) {
            tienePermiso = userdata.permissions.some((p) => p.slug === 'enviarRipsSispro');
        } else if (userdata.up && userdata.up.includes('enviarRipsSispro')) {
            tienePermiso = true;
        }
        setPuedeGestionarSispro(!!tienePermiso);
    }, [show]);

    if (!show) return null;

    const { 
        rips_id, 
        factura_id, 
        tipo, 
        estado, 
        factura_estado, 
        factura_tipo_factura, 
        factura_prefijo,
        factura_numero,
        factura_etiqueta,
        users_id: ripsUsersId,
        fecha,
        paciente_nombre,
        paciente_tipo_doc,
        paciente_numero_doc,
        profesional_nombre,
        tipo_registro,
        titulo_modal,
        subtitulo_modal,
    } = ripsData;

    const datosPacienteCompletos = pacienteTieneDatosCompletosParaFactura(pacienteDatos);
    const mostrarIconoPaciente = !cargandoPaciente && pacienteDatos != null;

    const tituloModal = titulo_modal || 'RIPS guardado correctamente';
    const subtituloModal = subtitulo_modal || 'Registro procesado y listo para gestión';

    const formatearFechaRips = (fechaValor) => {
        if (!fechaValor) return '—';
        const texto = String(fechaValor);
        const soloFecha = texto.includes('T') ? texto.split('T')[0] : texto.slice(0, 10);
        const date = new Date(`${soloFecha}T12:00:00`);
        if (Number.isNaN(date.getTime())) return soloFecha;
        return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const getNumeroFacturaTexto = () => {
        if (!factura_id) return 'No aplica';
        if (factura_etiqueta) return factura_etiqueta;
        return getEtiquetaFactura(factura_prefijo, factura_numero, factura_id) || `#${factura_id}`;
    };

    const getPacienteTexto = () => {
        if (!paciente_nombre) return '—';
        const doc = [paciente_tipo_doc, paciente_numero_doc].filter(Boolean).join(' ');
        return doc ? `${paciente_nombre} · ${doc}` : paciente_nombre;
    };

    const labelEstadoFactura = (codigo) => {
        const n = codigo === null || codigo === undefined ? NaN : parseInt(codigo, 10);
        const map = {
            0: 'Borrador DIAN',
            1: 'Contado DIAN',
            2: 'Crédito DIAN',
            3: 'Anulada',
            4: 'Contado (no electrónica)',
            5: 'Crédito (no electrónica)',
            6: 'Error DIAN',
            7: 'Borrador RIPS DIAN',
        };
        return map[n] || 'Desconocido';
    };

    const formatearFechaRadicacion = (fecha) => {
        if (!fecha) return '';
        const texto = String(fecha);
        if (texto.includes('T')) {
            const [dia, horaRaw] = texto.split('T');
            const hora = horaRaw.replace(/\.\d+.*$/, '').substring(0, 8);
            return `${dia} ${hora}`;
        }
        return texto.substring(0, 19);
    };

    const validarEstadoFactura = async () => {
        setValidandoFactura(true);
        try {
            const response = await axios.post('/rips-sispro/validar-factura', {
                factura_id: factura_id
            });
            setFacturaValida(response.data);
        } catch (error) {
            console.error('Error validando factura:', error);
            setFacturaValida({ 
                valida: false, 
                motivo: 'Error al validar estado de factura' 
            });
        } finally {
            setValidandoFactura(false);
        }
    };

    const ejecutarEnvioSispro = async () => {
        setEnviando(true);
        try {
            const response = await axios.post('/rips-sispro/enviar', { rips_id });
            setEstadoEnvio('enviado');
            setCuv(response.data.cuv);
            setFechaRadicacion(response.data.fecha_radicacion);
            if (typeof onEnvioExitoso === 'function') {
                onEnvioExitoso(response.data);
            }
            Swal.fire({
                icon: 'success',
                title: '¡RIPS Enviado!',
                html: `
                    <p>El RIPS fue enviado exitosamente a SISPRO</p>
                    <hr>
                    <p><strong>CUV:</strong> ${response.data.cuv}</p>
                    <p><strong>Fecha de Radicación:</strong> ${formatearFechaRadicacion(response.data.fecha_radicacion)}</p>
                `,
                confirmButtonText: 'Aceptar'
            });
        } catch (error) {
            console.error('Error enviando RIPS:', error);
            setEstadoEnvio('error');
            if (error.response?.data?.requiere_configuracion) {
                setShowModalConfigSispro(true);
                return;
            }
            const data = error.response?.data || {};
            const paso = data.paso || null;
            let errorMessage = data.message || 'Error al enviar RIPS a SISPRO';
            if (paso === 'login_sispro') {
                errorMessage = data.message || 'El login en SISPRO falló. Verifique contraseña, NIT y tipo de usuario del profesional configurado.';
            } else if (paso === 'xml_faltante') {
                errorMessage = data.message || 'Falta el XML de la factura en la DIAN. Envíe la factura a la DIAN antes de radicar el RIPS.';
            } else if (paso === 'envio_rips') {
                errorMessage = data.message || 'SISPRO rechazó el paquete RIPS. Revise los detalles de validación.';
            }
            setErrorSispro({
                titulo: paso === 'login_sispro'
                    ? 'Error de autenticación SISPRO'
                    : paso === 'xml_faltante'
                        ? 'Factura sin XML DIAN'
                        : 'Error al enviar a SISPRO',
                mensaje: errorMessage,
                status: error.response?.status,
                paso,
                detalles: data.detalles || data || null,
            });
            setShowModalErrorSispro(true);
        } finally {
            setEnviando(false);
        }
    };

    const handleEnviarSispro = async () => {
        if (!puedeGestionarSispro) {
            await Swal.fire({
                icon: 'warning',
                title: 'Sin permiso',
                text: 'No tiene permiso para enviar a SISPRO.',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        if (!datosPacienteCompletos) {
            await Swal.fire({
                icon: 'warning',
                title: 'Datos del paciente incompletos',
                text: 'Complete los datos obligatorios del paciente (nombre, documento, celular, email, dirección, país, municipio y zona) antes de enviar el RIPS a SISPRO.',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Confirmar envío
        const result = await Swal.fire({
            title: '¿Enviar RIPS a SISPRO?',
            text: 'Se enviará el RIPS al sistema SISPRO del Ministerio de Salud',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#323a45',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sí, enviar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        setEnviando(true);
        try {
            const verif = await axios.post('/rips-sispro/datos-configuracion', { rips_id });
            if (!verif.data?.configurado) {
                setShowModalConfigSispro(true);
                return;
            }
            await ejecutarEnvioSispro();
        } catch (error) {
            console.error('Error verificando configuración SISPRO:', error);
            if (error.response?.data?.requiere_configuracion) {
                setShowModalConfigSispro(true);
                return;
            }
            setErrorSispro({
                titulo: 'Error al verificar SISPRO',
                mensaje: error.response?.data?.message || 'No se pudo verificar la configuración SISPRO',
                status: error.response?.status,
                detalles: error.response?.data || null,
            });
            setShowModalErrorSispro(true);
        } finally {
            setEnviando(false);
        }
    };

    const getTipoTexto = () => {
        if (tipo === 'fev') return 'RIPS con Factura Electrónica';
        if (tipo === 'convenio') return 'RIPS por Convenio';
        if (tipo === 'sin_fev') return 'RIPS sin Factura';
        return 'RIPS';
    };

    const getEstadoEnvioBadge = () => {
        if (estadoEnvio === 'enviado') {
            return <span className="badge badge-outline-secondary"><i className="fas fa-check-circle mr-1"></i>Enviado a SISPRO</span>;
        }
        if (estadoEnvio === 'error') {
            return <span className="badge badge-outline-danger"><i className="fas fa-exclamation-circle mr-1"></i>Error al enviar</span>;
        }
        if (factura_id && facturaValida && !facturaValida.valida) {
            return <span className="badge badge-outline-warning"><i className="fas fa-exclamation-triangle mr-1"></i>Factura pendiente</span>;
        }
        return <span className="badge badge-outline-secondary"><i className="fas fa-clock mr-1"></i>Pendiente de envío</span>;
    };

    const botonEnviarHabilitado = () => {
        if (!puedeGestionarSispro) return false;
        if (enviando || estadoEnvio === 'enviado') return false;
        if (!datosPacienteCompletos) return false;
        
        // Sin factura: siempre habilitado si tiene permiso
        if (!factura_id) return true;
        
        // Con factura electrónica: debe estar enviada a DIAN (estados 1 o 2)
        // Estados: 0=borrador DIAN, 1=contado DIAN, 2=crédito DIAN, 3=anulada, 
        // 4=contado no electrónica, 5=crédito no electrónica, 6=error DIAN, 7=borrador RIPS DIAN
        const estadoFactura = facturaValida?.estado_factura ?? factura_estado;
        const esNoElectronica = factura_tipo_factura === 'no_electronica' || estadoFactura === 4 || estadoFactura === 5;
        
        if (esNoElectronica) {
            return true; // Factura no electrónica: siempre habilitado
        }
        
        // Factura electrónica: solo habilitado si ya fue enviada (estados 1 o 2)
        return estadoFactura === 1 || estadoFactura === 2;
    };

    const getTooltipEnviar = () => {
        if (!puedeGestionarSispro) return 'Sin permiso: enviarRipsSispro';
        if (estadoEnvio === 'enviado') return 'Este RIPS ya fue enviado a SISPRO';
        if (enviando) return 'Enviando...';
        if (!datosPacienteCompletos) return 'Faltan datos del paciente para enviar el RIPS';
        
        if (factura_id) {
            const estadoFactura = facturaValida?.estado_factura ?? factura_estado;
            const esNoElectronica = factura_tipo_factura === 'no_electronica' || estadoFactura === 4 || estadoFactura === 5;
            
            if (!esNoElectronica && estadoFactura !== 1 && estadoFactura !== 2) {
                return 'Debe enviar primero la factura electrónica a la DIAN';
            }
        }
        
        return 'Enviar RIPS a SISPRO';
    };

    const modalErrorSispro = showModalErrorSispro && errorSispro && (
        <div
            className="modal fade show lf-ds-modal"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 10060 }}
            tabIndex="-1"
            role="dialog"
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" role="document">
                <div className="modal-content border-0 shadow">
                    <div className="modal-header lf-ds-modal-head align-items-center">
                        <h5 className="modal-title mb-0 d-flex align-items-center font-weight-bold">
                            <span
                                className="d-inline-flex align-items-center justify-content-center rounded-circle mr-2"
                                style={{ width: 36, height: 36, background: 'rgba(220, 53, 69, 0.12)', color: '#dc3545' }}
                            >
                                <i className="fas fa-exclamation"></i>
                            </span>
                            {errorSispro.titulo}
                        </h5>
                        <button
                            type="button"
                            className="close"
                            onClick={() => setShowModalErrorSispro(false)}
                            aria-label="Cerrar"
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body pt-3 pb-2">
                        <div className="lf-ds-alert-danger-soft mb-3">
                            <strong className="d-block mb-1">Detalle</strong>
                            <span style={{ lineHeight: 1.5 }}>{errorSispro.mensaje}</span>
                            {errorSispro.status && (
                                <p className="mb-0 mt-2 small text-muted">
                                    Código HTTP: {errorSispro.status}
                                    {errorSispro.paso ? ` · Paso: ${errorSispro.paso}` : ''}
                                </p>
                            )}
                        </div>
                        {errorSispro.detalles && (
                            <div className="lf-ds-dian-detail-panel mb-0">
                                <div className="lf-dian-label">Respuesta del servidor</div>
                                <pre
                                    className="mb-0"
                                    style={{
                                        backgroundColor: '#f4f6f8',
                                        padding: '12px 14px',
                                        borderRadius: '8px',
                                        maxHeight: '320px',
                                        overflow: 'auto',
                                        fontSize: '12px',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, monospace',
                                    }}
                                >
                                    {JSON.stringify(errorSispro.detalles, null, 2)}
                                </pre>
                            </div>
                        )}
                        <p className="text-muted small mt-3 mb-0">
                            Si la contraseña SISPRO fue creada antes de hoy, edítela en Configuración → Usuarios SISPRO y guárdela de nuevo.
                            Use &quot;Reenviar a SISPRO&quot; solo si confirma que el documento no existe en el ministerio.
                        </p>
                    </div>
                    <div className="modal-footer border-top bg-light">
                        <button type="button" className="btn btn-secondary" onClick={() => setShowModalErrorSispro(false)}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
        <ModalConfigurarSispro
            show={showModalConfigSispro}
            onClose={() => setShowModalConfigSispro(false)}
            ripsId={rips_id}
            ripsUsersId={ripsUsersId}
            onGuardado={ejecutarEnvioSispro}
        />
        {modalErrorSispro}
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content modal-rips-exito">
                    <div className="modal-header modal-rips-exito__header">
                        <div className="d-flex align-items-center flex-grow-1 min-width-0">
                            <span className="modal-rips-exito__header-badge">
                                <i className="fas fa-check-circle"></i>
                            </span>
                            <div className="min-width-0">
                                <h4 className="modal-title mb-0">{tituloModal}</h4>
                                {subtituloModal && (
                                    <p className="modal-rips-exito__subtitle mb-0">{subtituloModal}</p>
                                )}
                            </div>
                        </div>
                        <button type="button" className="close ml-3" onClick={onClose} aria-label="Cerrar">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div className="modal-rips-exito__status-bar">
                        <span className="modal-rips-exito__status-label">
                            <i className="fas fa-info-circle mr-1"></i>
                            Información del registro RIPS
                        </span>
                    </div>

                    <div className="modal-body modal-rips-exito__body">
                        <div className="modal-rips-info-grid">
                            <div className="modal-rips-info-card">
                                <div className="modal-rips-info-card__label">Fecha</div>
                                <div className="modal-rips-info-card__value">{formatearFechaRips(fecha)}</div>
                            </div>
                            <div className="modal-rips-info-card">
                                <div className="modal-rips-info-card__label">Paciente</div>
                                <div className="d-flex align-items-start justify-content-between">
                                    <div className="modal-rips-info-card__value min-width-0" title={getPacienteTexto()}>{getPacienteTexto()}</div>
                                    {mostrarIconoPaciente && (
                                        <span
                                            className={`patient-status-icon ml-2 ${datosPacienteCompletos ? 'patient-status-icon--ok' : 'patient-status-icon--pending'}`}
                                            title={datosPacienteCompletos
                                                ? 'Datos del paciente completos para enviar RIPS'
                                                : 'Faltan datos del paciente para enviar RIPS'}
                                        >
                                            <i className="fas fa-user"></i>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="modal-rips-info-card">
                                <div className="modal-rips-info-card__label">Tipo</div>
                                <div className="modal-rips-info-card__value">{getTipoTexto()}</div>
                                {tipo_registro && (
                                    <div className="modal-rips-info-card__meta">{tipo_registro}</div>
                                )}
                            </div>
                            <div className="modal-rips-info-card">
                                <div className="modal-rips-info-card__label">N° de factura</div>
                                <div className="modal-rips-info-card__value">{getNumeroFacturaTexto()}</div>
                                {factura_id && (factura_tipo_factura || factura_estado != null) && (
                                    <div className="modal-rips-info-card__meta">
                                        {factura_tipo_factura === 'no_electronica' ? 'No electrónica' : 'Electrónica'}
                                        {' · '}
                                        {labelEstadoFactura(
                                            facturaValida && facturaValida.estado_factura != null
                                                ? facturaValida.estado_factura
                                                : factura_estado
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-rips-info-card">
                                <div className="modal-rips-info-card__label">Profesional</div>
                                <div className="modal-rips-info-card__value">{profesional_nombre || '—'}</div>
                            </div>
                            <div className="modal-rips-info-card">
                                <div className="modal-rips-info-card__label">Envío SISPRO</div>
                                <div className="modal-rips-info-card__value modal-rips-info-card__value--badge">
                                    {getEstadoEnvioBadge()}
                                </div>
                            </div>
                        </div>

                        {cuv && (
                            <div className="alert modal-rips-alert-soft mt-3 mb-0">
                                <p className="mb-1"><strong>CUV:</strong> <span className="small text-break">{cuv}</span></p>
                                {fechaRadicacion && (
                                    <p className="mb-0"><strong>Fecha de radicación:</strong> {formatearFechaRadicacion(fechaRadicacion)}</p>
                                )}
                            </div>
                        )}

                        {factura_id && facturaValida && !facturaValida.valida && (
                            <div className="alert modal-rips-alert-caution mt-3 mb-0">
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                {facturaValida.motivo}
                            </div>
                        )}

                        {mostrarIconoPaciente && !datosPacienteCompletos && (
                            <div className="alert modal-rips-alert-caution mt-3 mb-0">
                                <i className="fas fa-user mr-2"></i>
                                Faltan datos del paciente para enviar el RIPS. Complete el perfil (celular, email, dirección, país, municipio y zona).
                            </div>
                        )}

                        <div className="modal-rips-actions">
                            <div className="modal-rips-actions__title">Acciones disponibles</div>
                            <div className={`modal-rips-actions__grid ${factura_id ? 'modal-rips-actions__grid--3' : 'modal-rips-actions__grid--2'}`}>
                                <button
                                    type="button"
                                    className="btn btn-rips-modal-solid modal-rips-action-btn"
                                    onClick={() => onDescargar(rips_id)}
                                    title="Descargar JSON"
                                >
                                    <span className="modal-rips-action-btn__main">
                                        <i className="fas fa-download"></i>
                                        Descargar
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    className={`btn modal-rips-action-btn ${botonEnviarHabilitado() ? 'btn-modal-rips-cta' : 'btn-secondary modal-rips-btn-enviar'}`}
                                    onClick={handleEnviarSispro}
                                    disabled={!botonEnviarHabilitado() || validandoFactura}
                                    title={getTooltipEnviar()}
                                >
                                    <span className="modal-rips-action-btn__main">
                                        {enviando ? (
                                            <><i className="fas fa-spinner fa-spin"></i> Enviando...</>
                                        ) : validandoFactura ? (
                                            <><i className="fas fa-spinner fa-spin"></i> Validando...</>
                                        ) : estadoEnvio === 'enviado' ? (
                                            <><i className="fas fa-check-circle"></i> Enviado</>
                                        ) : (
                                            <><i className="fas fa-paper-plane"></i> Enviar SISPRO</>
                                        )}
                                    </span>
                                </button>
                                {factura_id && (
                                    <Link
                                        to={`/informacion-factura/${factura_id}`}
                                        className="btn btn-outline-rips-slate modal-rips-action-btn"
                                        title="Ver factura"
                                    >
                                        <span className="modal-rips-action-btn__main">
                                            <i className="fas fa-file-invoice"></i>
                                            Ver factura
                                        </span>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}

export default ModalRipsExito;
